/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the 'Software'), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED 'AS IS', WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import session from '../../../../share/session';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  WAIT: 'wait',
  SUCCESS: 'success',
  FAILED: 'failed',
  CONNECTION_FAILED: 'connectionFailed'
};

/** GETルーチン（取得）処理結果 */
const RESULT = {
  CONNECT_COMPLETE_MODAL: '1000',
  NOT_CONNECT_MODAL: '1001'
};

export default {
  name: 'requirement040',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      // ダイアログフラグ
      modal_requirement_040: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: (state) => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.CONNECTION_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_040 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** NonDefaultSession処理 */
    async standby() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.phase = PHASE.WAIT;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    // ルーチン実行、ルーチン結果確認
    async execute() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // response結果で画面遷移する
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              switch (response.data.user_data.routine_status) {
                case RESULT.CONNECT_COMPLETE_MODAL:
                  // 接続確認完了画面
                  this.phase = PHASE.SUCCESS;
                  break;
                case RESULT.NOT_CONNECT_MODAL:
                  // 未接続画面
                  this.phase = PHASE.CONNECTION_FAILED;
                  break;
                default:
                  this.showUnexpectedError();
                  break;
              }
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exit() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // DefaultSession遷移
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      if (this.phase === PHASE.FAILED) {
        // Wizardを閉じる
        this.closeWizard();
        return;
      }

      this.phase = PHASE.FAILED;
    },

    /** OKボタン押下 */
    async exitProcess() {
      await this.exit();
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_040 = false;
    },
  },

  async mounted() {
    this.init();
    this.standby();
  },
};
